<template>
  <!--
    매장 폼의 탭 — 저장 본문(`form`) 하나를 나눠 그린다. 탭 이름·순서는 `utils/storeDetailForm` 의 STORE_FORM_TABS 와 같다
    (단위 시험 `storeOpsParity.test.js` 가 소스로 고정한다). 화면 고유의 탭(샵인샵·외부 채널 연동·우리동네TV 쿠폰)은
    기본 슬롯으로 받아 이 여섯 탭 뒤에 붙인다.
  -->
  <el-tabs class="store-form-tabs" :value="value" @input="$emit('input', $event)">
    <el-tab-pane :label="$t('매장 정보 설정')" name="info">
      <StoreInfoFormPanel
        :value="form"
        :formError="formError"
        :custSearch="custSearch"
        :cust="cust"
        @form-error-update="$emit('form-error-update', $event)" />
    </el-tab-pane>

    <el-tab-pane :label="$t('매장 소개·원산지')" name="content">
      <StoreContentPanel :value="form" :formError="formError" />
    </el-tab-pane>

    <el-tab-pane :label="$t('알림톡 설정')" name="alarm">
      <AlarmTalkSettingPanel :value="form" :formError="formError" />
    </el-tab-pane>

    <el-tab-pane :label="$t('영업 시간 설정')" name="time">
      <OperationTimeSettingPanel :value="form" :formError="formError" />
    </el-tab-pane>

    <el-tab-pane :label="$t('배달 설정')" name="delivery">
      <DeliverySettingPanel
        :value="form"
        :formError="formError"
        :deliveryAgencyOptions="deliveryAgencyOptions"
        :brandCodeOptionsMap="brandCodeOptionsMap"
        v-on="deliveryListeners" />
    </el-tab-pane>

    <el-tab-pane :label="$t('사업자정보')" name="cust">
      <BusinessInfoFormPanel :value="form" :formError="formError" />
    </el-tab-pane>

    <slot />
  </el-tabs>
</template>

<script>
import AlarmTalkSettingPanel from '@/components/store/AlarmTalkSettingPanel.vue'
import BusinessInfoFormPanel from '@/components/store/BusinessInfoFormPanel.vue'
import DeliverySettingPanel from '@/components/store/DeliverySettingPanel.vue'
import OperationTimeSettingPanel from '@/components/store/OperationTimeSettingPanel.vue'
import StoreContentPanel from '@/components/store/StoreContentPanel.vue'
import StoreInfoFormPanel from '@/components/store/StoreInfoFormPanel.vue'

/** 배달 설정 패널의 행 추가·삭제 — 폼을 고치는 일은 화면(`storeFormMixin`)의 것이라 그대로 올려 보낸다 */
const DELIVERY_EVENTS = Object.freeze([
  'addDeliveryFee',
  'removeDeliveryFee',
  'addDeliveryAgency',
  'removeDeliveryAgency',
])

/**
 * **매장 폼의 탭 한 곳** — 매장 정보 관리(`StoreInfoView`)·매장 관리(`StoreListView`)·사업장 트리(`StoreFormContainer`)가
 * 같은 탭을 이 컴포넌트로 그린다(목록은 [STORE_FORM_TABS]).
 *
 * 화면마다 탭을 적던 동안 4월 재구성(`e081866`)이 두 화면만 고쳐, 사업장 관리자·관리점·운영사가 쓰는 매장 관리에서
 * 「매장 소개·원산지」·「알림톡 설정」이 빠졌다(R-S). 그래서 탭의 구성은 여기 하나다 — 화면은 무엇을 더 붙이는지만 정한다.
 *
 * 이 컴포넌트는 폼을 고치지 않는다. 칸은 패널들이 그 자리에서 고치고(종전과 같다), 폼 객체를 바꾸는 일(배달 행 추가·삭제,
 * 언어 행 오류 칸)은 이벤트로 화면에 올린다. 역할별 숨김은 패널이 갖는다(「오더톡 보이지 않기」는 내부 관리자만 —
 * `AlarmTalkSettingPanel`, 대행 계정의 잠긴 칸 — `StoreInfoFormPanel`·`BusinessInfoFormPanel`).
 */
export default {
  name: 'StoreFormTabs',

  components: {
    AlarmTalkSettingPanel,
    BusinessInfoFormPanel,
    DeliverySettingPanel,
    OperationTimeSettingPanel,
    StoreContentPanel,
    StoreInfoFormPanel,
  },

  props: {
    /** 펼친 탭 이름(`v-model`) — 화면의 `activiTabStore` */
    value: { default: undefined },
    /** 매장 폼 — 저장 본문 그대로다. 패널들이 칸을 그 자리에서 고친다 */
    form: { type: Object, required: true },
    formError: { type: Object, required: true },
    deliveryAgencyOptions: { type: Array, default: () => [] },
    brandCodeOptionsMap: { type: Object, default: () => ({}) },
    /** 소속 사업장 칸의 재료 — 그 칸을 그리는 사업장 트리만 넘긴다(`StoreInfoFormPanel`) */
    custSearch: { default: undefined },
    cust: { default: undefined },
  },

  computed: {
    deliveryListeners() {
      return DELIVERY_EVENTS.reduce(
        (listeners, event) => Object.assign(listeners, { [event]: (...args) => this.$emit(event, ...args) }),
        {}
      )
    },
  },
}
</script>
